import React, { useRef } from 'react';

import {
    FloorGameCard,
    type FloorGameCardProps,
    FloorGameTicks,
    FloorGameTicksMeterSize,
    useFloorGameTicks,
} from '@pkg/components';
import { getFloorGameProps } from '@pkg/components/Sections/Groups/GroupCard';
import { convertEthToWei, convertWeiToEth, DEFAULT_ETH_MAX_DECIMALS } from '@pkg/components/Sections/Groups/helpers';
import { useGetFloorGameData } from '@pkg/hooks';
import { EthTokenStardard, FloorGameActivity, FloorGameBlockchain, FloorGameCrypto } from '@pkg/types/floorGame';

import { type GroupDataItem, type GroupItem } from '@app/client/common/structs/groups/groups';
import { type CurrencyConversionRate } from '@app/client/common/structs/nft/currencies';

export interface PodNftCardProps {
    item: GroupItem;
    owner: GroupDataItem['owner'];
    currencyConversionRate: CurrencyConversionRate;
    lastSale?: number;
    compact?: boolean;
}

export function PodNftCard({ item, owner, currencyConversionRate, lastSale, compact = false }: PodNftCardProps) {
    const cardRef = useRef<HTMLDivElement>(null);

    const slug = item.item_type.Nft.slug || '';
    const floorPriceWei = item.item_type.Nft.floor_price ? convertEthToWei(item.item_type.Nft.floor_price) : 0;
    const floorGameData = useGetFloorGameData({
        contractAddress: item.item_type.Nft.contract_address || '',
        slug,
        floorPriceWei,
    });

    // Temporary injecting floor price from hook while
    // this component is being merged with FloorGameCard
    item.item_type.Nft.floor_price = floorGameData.floorPriceEth ?? item.item_type.Nft.floor_price;

    const collection = item.item_type.Nft.slug || undefined;
    const contractAddress = item.item_type.Nft.contract_address || '';

    const listingsItems = floorGameData.listings?.items ?? [];
    const listingsCount = floorGameData.listings?.count ?? listingsItems.length;

    // Temporary until we have a better solution for
    // incrementally loading data > UI.
    const estimation = Boolean(floorGameData.listings && !listingsItems.length && !listingsCount);

    const nextNfts =
        listingsItems.map((listing) => {
            return {
                id: `TickFloorGame-${collection}-${listing.token_id}}`,
                name: '',
                contract: {
                    address: '',
                    blockchain: FloorGameBlockchain.Ethereum,
                    standard: EthTokenStardard.Erc721,
                    creator: {
                        name: '',
                        source: '',
                        url: '',
                    },
                    name: '',
                },
                collection: {
                    name: '',
                },
                asset: {
                    imageUrl: listing.active_listing_token?.image_url || undefined,
                    videoUrl: listing.active_listing_token?.animation_url || undefined,
                },
                value: {
                    currency: FloorGameCrypto.Ethereum,
                    price: convertWeiToEth(listing.wei_price, DEFAULT_ETH_MAX_DECIMALS),
                },
            };
        }) || [];

    const floorGameProps = getFloorGameProps({ item, owner, currencyConversionRate, lastSale });

    // TODO: How do we determine `activity`?
    const activity = FloorGameActivity.Quiet;
    // This might eventually be unique per-collection
    const currency = FloorGameCrypto.Ethereum;

    // TODO: I assume it is intentional that we are not returning
    // `lastSale` and `nextNfts` from `getFloorGameProps()`?
    const constructedFloorGameProps: FloorGameCardProps = {
        ...floorGameProps,
        lastSale,
        nextNfts,
    };

    // We should reliably have a `ContactAddress`.
    const ticksMeterId = contractAddress || collection || 'FallbackId';
    const ticks = useFloorGameTicks(ticksMeterId, listingsItems, {
        size: compact ? FloorGameTicksMeterSize.Compact : FloorGameTicksMeterSize.Normal,
        totalListingsInTier: listingsCount,
        activity,
    });

    const ticksMarkup = estimation ? null : (
        <FloorGameTicks
            ticks={ticks}
            tier={floorGameProps.currentTier}
            currency={currency}
            loading={!floorGameData.listings}
        />
    );

    // TODO: Need to pass `notificationPortalRef` once we are ready.
    return (
        <FloorGameCard
            ref={cardRef}
            {...constructedFloorGameProps}
            listingsCount={listingsCount}
            compact={compact}
            estimation={estimation}
        >
            {ticksMarkup}
        </FloorGameCard>
    );
}
